import { useCallback, useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isSuperAdminContextUser } from '../authStorage';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import {
  createSuperAdminDecision,
  deleteSuperAdminDecision,
  fetchSuperAdminDecisionCompanies,
  fetchSuperAdminDecisions,
  fetchSuperAdminDecisionTypes,
  updateSuperAdminDecision,
} from '../superAdminDecisionsApi';
import './BranchesPage.css';
import './DecisionsPage.css';
import './Modal.css';
import './UsersPage.css';

const PAGE_SIZE = 10;
const emptyForm = () => ({
  companyId: '',
  decisionTypeId: '',
  title: '',
  description: '',
  status: 'pending',
  priority: 'medium',
  deadline: '',
  totalMembers: '',
});

export default function SuperAdminDecisionsPage() {
  const { direction } = useLanguage();
  const t = useT();

  const [accessDenied, setAccessDenied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [decisionTypes, setDecisionTypes] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const [filters, setFilters] = useState({
    q: '',
    companyId: '',
    decisionTypeId: '',
    status: '',
    priority: '',
    fromDate: '',
    toDate: '',
    sortBy: 'createdAt',
    sortDir: 'desc',
  });

  const loadData = useCallback(async (page = 1, nextFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const [listRes, companyRes, typeRes] = await Promise.all([
        fetchSuperAdminDecisions({ page, pageSize: PAGE_SIZE, ...nextFilters }),
        fetchSuperAdminDecisionCompanies(),
        fetchSuperAdminDecisionTypes(nextFilters.companyId || ''),
      ]);
      setRows(listRes.items);
      setCompanies(companyRes);
      setDecisionTypes(typeRes);
      setCurrentPage(listRes.page);
      setTotalRows(listRes.total);
      setTotalPages(listRes.totalPages);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('decisions.saLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!getAccessToken()) {
      window.location.assign('/login');
      return;
    }
    if (!isSuperAdminContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadData(1, filters);
    // Run on mount and whenever the filters change — NOT on every render.
    // (`loadData` is intentionally excluded; `useT()` returns a new function
    //  each render which previously caused an infinite refresh loop.)
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => ({
    pending: rows.filter((x) => x.status === 'pending').length,
    approved: rows.filter((x) => x.status === 'approved').length,
    rejected: rows.filter((x) => x.status === 'rejected').length,
  }), [rows]);

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    if (key === 'companyId') next.decisionTypeId = '';
    setFilters(next);
    loadData(1, next);
  };

  const resetFilters = () => {
    const next = { q: '', companyId: '', decisionTypeId: '', status: '', priority: '', fromDate: '', toDate: '', sortBy: 'createdAt', sortDir: 'desc' };
    setFilters(next);
    loadData(1, next);
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEdit = async (item) => {
    const types = await fetchSuperAdminDecisionTypes(item.companyId);
    setDecisionTypes(types);
    setEditId(item.id);
    setForm({
      companyId: String(item.companyId),
      decisionTypeId: item.decisionTypeId || '',
      title: item.title || '',
      description: item.description || '',
      status: item.status || 'pending',
      priority: item.priority || 'medium',
      deadline: item.deadline || '',
      totalMembers: item.totalMembers ? String(item.totalMembers) : '',
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm(emptyForm());
  };

  const onCompanyChangeInForm = async (value) => {
    setForm((p) => ({ ...p, companyId: value, decisionTypeId: '' }));
    const types = await fetchSuperAdminDecisionTypes(value || '');
    setDecisionTypes(types);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!form.companyId || !form.decisionTypeId || !form.title.trim()) {
      setError(t('decisions.saRequired'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        companyId: Number(form.companyId),
        decisionTypeId: Number(form.decisionTypeId),
        title: form.title.trim(),
        description: form.description.trim() || undefined,
        status: form.status,
        priority: form.priority,
        deadline: form.deadline || undefined,
        totalMembers: Number(form.totalMembers || 0),
      };
      if (editId) await updateSuperAdminDecision(editId, payload);
      else await createSuperAdminDecision(payload);
      closeModal();
      await loadData(currentPage, filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : (editId ? t('decisions.saUpdateFailed') : t('decisions.saCreateFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(t('decisions.saConfirmDelete'))) return;
    try {
      await deleteSuperAdminDecision(item.id);
      await loadData(currentPage, filters);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('decisions.saDeleteFailed'));
    }
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    loadData(page, filters);
  };

  if (accessDenied) {
    return (
      <div className="page" dir={direction}>
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t('decisions.title')}</span>
            <span className="page__topbar-sub">{t('decisions.saDeniedSub')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page users-page" dir={direction}>
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editId ? t('decisions.saModalEdit') : t('decisions.saModalAdd')}
        subtitle={t('decisions.saSubtitle')}
        icon={<Icon name="decisions" size={18} color="#fff" />}
        size="lg"
        variant="drawer-left"
        headerActions={(
          <button type="submit" form="decision-superadmin-form" className="btn btn--primary" disabled={submitting}>
            {submitting ? (editId ? t('decisions.saSaving') : t('decisions.saAdding')) : (editId ? t('decisions.saSaveChanges') : t('common.save'))}
          </button>
        )}
        footer={<button type="button" className="btn btn--ghost" disabled={submitting} onClick={closeModal}>{t('common.cancel')}</button>}
      >
        <form id="decision-superadmin-form" className="mform" onSubmit={submitForm}>
          {error && <div className="mform__error" style={{ padding: '10px 12px', background: '#ffebee', borderRadius: 10 }}>{error}</div>}
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t('decisions.lblBranch')} <span>*</span></label>
              <select className="mform__select" value={form.companyId} onChange={(e) => onCompanyChangeInForm(e.target.value)}>
                <option value="">{t('decisions.saSelectCompany')}</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="mform__field">
              <label className="mform__label">{t('decisions.lblDecisionType')} <span>*</span></label>
              <select className="mform__select" value={form.decisionTypeId} onChange={(e) => setForm((p) => ({ ...p, decisionTypeId: e.target.value }))}>
                <option value="">{t('decisions.saSelectType')}</option>
                {decisionTypes.filter((x) => !form.companyId || String(x.companyId) === String(form.companyId)).map((x) => (
                  <option key={x.id} value={x.id}>{x.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mform__field mform__field--full">
            <label className="mform__label">{t('decisions.lblTitle')} <span>*</span></label>
            <input className="mform__input" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="mform__field mform__field--full">
            <label className="mform__label">{t('decisions.lblDescription')}</label>
            <textarea className="mform__input" rows={4} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t('decisions.lblStatus')}</label>
              <select className="mform__select" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
                <option value="pending">{t('decisions.statusPending')}</option>
                <option value="approved">{t('common.approved')}</option>
                <option value="rejected">{t('common.rejected')}</option>
              </select>
            </div>
            <div className="mform__field">
              <label className="mform__label">{t('decisions.lblPriority')}</label>
              <select className="mform__select" value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}>
                <option value="low">{t('decisions.priorityLow')}</option>
                <option value="medium">{t('decisions.priorityMedium')}</option>
                <option value="high">{t('decisions.priorityHigh')}</option>
              </select>
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t('decisions.lblDeadline')}</label>
              <input className="mform__input" type="date" value={form.deadline} onChange={(e) => setForm((p) => ({ ...p, deadline: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t('decisions.lblTotalMembers')}</label>
              <input className="mform__input" type="number" min="0" value={form.totalMembers} onChange={(e) => setForm((p) => ({ ...p, totalMembers: e.target.value }))} />
            </div>
          </div>
        </form>
      </Modal>

      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="decisions" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t('decisions.title')}
            </span>
            <span className="list-page-card__sub">{t('decisions.saSubtitle')}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              <Icon name="plus" size={14} color="#fff" />
              {t('decisions.new')}
            </button>
          </div>
        </header>

        <div className="page__stats">
          <div className="page__stat-chip page__stat-chip--total"><Icon name="decisions" size={13} /> {t('decisions.totalStat')}: {totalRows}</div>
          <div className="page__stat-chip page__stat-chip--active"><Icon name="clock" size={13} /> {t('decisions.statusPending')}: {stats.pending}</div>
          <div className="page__stat-chip page__stat-chip--new"><Icon name="check-circle" size={13} /> {t('common.approved')}: {stats.approved}</div>
        </div>

        <div className="page__filters" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div className="page__search" style={{ minWidth: 280, flex: '1 1 320px' }}>
            <Icon name="search" size={14} color="#90a4ae" />
            <input className="page__search-input" value={filters.q} onChange={(e) => updateFilter('q', e.target.value)} placeholder={t('decisions.searchPh')} />
          </div>
          <select className="page__filter-select" value={filters.companyId} onChange={(e) => updateFilter('companyId', e.target.value)}>
            <option value="">{t('decisions.allBranches')}</option>
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className="page__filter-select" value={filters.decisionTypeId} onChange={(e) => updateFilter('decisionTypeId', e.target.value)}>
            <option value="">{t('decisions.saAllTypes')}</option>
            {decisionTypes.filter((x) => !filters.companyId || String(x.companyId) === String(filters.companyId)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
          <select className="page__filter-select" value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
            <option value="">{t('decisions.saAllStatuses')}</option>
            <option value="pending">{t('decisions.statusPending')}</option>
            <option value="approved">{t('common.approved')}</option>
            <option value="rejected">{t('common.rejected')}</option>
          </select>
          <select className="page__filter-select" value={filters.priority} onChange={(e) => updateFilter('priority', e.target.value)}>
            <option value="">{t('decisions.saAllPriorities')}</option>
            <option value="low">{t('decisions.priorityLow')}</option>
            <option value="medium">{t('decisions.priorityMedium')}</option>
            <option value="high">{t('decisions.priorityHigh')}</option>
          </select>
          <input className="page__filter-select" type="date" value={filters.fromDate} onChange={(e) => updateFilter('fromDate', e.target.value)} placeholder={t('decisions.saFromDate')} />
          <input className="page__filter-select" type="date" value={filters.toDate} onChange={(e) => updateFilter('toDate', e.target.value)} placeholder={t('decisions.saToDate')} />
          <select className="page__filter-select" value={filters.sortBy} onChange={(e) => updateFilter('sortBy', e.target.value)}>
            <option value="createdAt">{t('decisions.saSortCreatedAt')}</option>
            <option value="title">{t('decisions.saSortTitle')}</option>
            <option value="company">{t('decisions.saSortCompany')}</option>
            <option value="status">{t('decisions.saSortStatus')}</option>
            <option value="priority">{t('decisions.saSortPriority')}</option>
            <option value="deadline">{t('decisions.saSortDeadline')}</option>
          </select>
          <select className="page__filter-select" value={filters.sortDir} onChange={(e) => updateFilter('sortDir', e.target.value)}>
            <option value="asc">{t('decisions.saAsc')}</option>
            <option value="desc">{t('decisions.saDesc')}</option>
          </select>
          <button type="button" className="btn btn--ghost" onClick={resetFilters}>{t('decisions.saReset')}</button>
        </div>

        {error && !modalOpen && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('common.loading')}</div>}

        {!loading && (
          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>#</th>
                  <th>{t('decisions.colTitle')}</th>
                  <th>{t('decisions.colBranch')}</th>
                  <th>{t('decisions.saColType')}</th>
                  <th>{t('decisions.colCreator')}</th>
                  <th>{t('decisions.colDate')}</th>
                  <th>{t('decisions.colStatus')}</th>
                  <th>{t('decisions.colPriority')}</th>
                  <th>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item, idx) => (
                  <tr key={item.id}>
                    <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                    <td>{item.title}</td>
                    <td>{item.companyName}</td>
                    <td>{item.decisionTypeName || '—'}</td>
                    <td>{item.creator}</td>
                    <td>{item.createdAt}</td>
                    <td>{item.status}</td>
                    <td>{item.priority}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" className="btn btn--ghost" style={{ padding: '6px 12px', fontSize: '0.78rem' }} onClick={() => openEdit(item)}>
                          <Icon name="edit" size={13} /> {t('common.edit')}
                        </button>
                        <button type="button" className="btn btn--ghost" style={{ padding: '6px 12px', fontSize: '0.78rem', color: '#c62828' }} onClick={() => deleteItem(item)}>
                          <Icon name="trash" size={13} /> {t('common.delete')}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <div className="users-empty">{t('decisions.saNoResults')}</div>}
            {totalPages > 1 && (
              <div className="pagination">
                <span>{t('common.showing')} {Math.min(totalRows, (currentPage - 1) * PAGE_SIZE + 1)}-{Math.min(totalRows, currentPage * PAGE_SIZE)} {t('common.of')} {totalRows}</span>
                <div className="pagination__pages">
                  <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button key={p} type="button" className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
                  ))}
                  <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
